import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Share2, Plus, Sparkles, Archive, Copy, Check, Settings2, Upload, FileText, Trash2, Images, X, Pencil, Target, Play, Clock, CalendarPlus } from "lucide-react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError, uploadFile } from "@/lib/api";
import CalendarMonth from "@/components/CalendarMonth";
import FeedGrid from "@/components/FeedGrid";
import StatusBadge from "@/components/StatusBadge";
import NewPublicationModal from "@/components/NewPublicationModal";
import PublicationsTable from "@/components/PublicationsTable";
import TodoCard from "@/components/TodoCard";
import { NotifyClientButton } from "@/components/NotifyClientButton";
import GraphismeClientFiche from "@/pages/GraphismeClientFiche";
import { GraphismeAccessCard, GraphismeUsersCard } from "@/components/graphisme/ClientGraphisme";
import { DriveExplorer } from "@/components/graphisme/Drive";
import { SocialNetworksAdmin } from "@/components/graphisme/SocialNetworksAdmin";
import { MetaConnect } from "@/components/graphisme/MetaConnect";
import WebProjectModal from "@/components/WebProjectModal";
import { legacyServices, prestationLabel, webStageLabel, webTypeLabel } from "@/lib/constants";

const CM_SUBNAV = [["apercu", "Vue d'ensemble CM"], ["calendrier", "Calendrier"], ["publications", "Publications"],
  ["contenus", "Contenu à fournir"], ["feed", "Feed"]];
const CM_TAB_IDS = CM_SUBNAV.map(([v]) => v);
const sectionOf = (t) => (t === "web" ? "web" : t === "graphisme" ? "graphisme" : CM_TAB_IDS.includes(t) ? "cm" : "overview");

function ParamSection({ title, desc, children, testId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm" data-testid={testId}>
      <button className="w-full flex items-center justify-between px-5 py-4 text-left" onClick={() => setOpen(!open)} data-testid={`${testId}-toggle`}>
        <div>
          <p className="font-heading font-bold text-sm">{title}</p>
          {desc && <p className="text-xs text-neutral-400 mt-0.5">{desc}</p>}
        </div>
        <span className="text-neutral-400 text-xs shrink-0">{open ? "▴" : "▾"}</span>
      </button>
      {open && <div className="px-5 pb-5 pt-4 border-t border-neutral-100">{children}</div>}
    </div>
  );
}

function WebDesignTab({ clientId }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    api.get("/web/projects", { params: { client_id: clientId } })
      .then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, [clientId]);
  return (
    <div className="space-y-4" data-testid="client-web-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading font-bold text-xl">Projets Web Design</h2>
        <Button onClick={() => setModalOpen(true)} className="gap-1.5 bg-[#0B132B] hover:bg-[#1C2A4F] text-white" data-testid="web-new-project-btn">
          <Plus className="w-4 h-4" /> Ajouter un nouveau projet
        </Button>
      </div>
      {projects === null ? null : projects.length === 0 ? (
        <div className="bg-white border border-dashed border-neutral-300 rounded-xl p-12 text-center" data-testid="web-empty">
          <p className="text-sm text-neutral-400">Aucun projet Web Design pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {projects.map((p) => (
            <button key={p.id} onClick={() => navigate(`/sites-web/projet/${p.id}`)} data-testid={`web-project-${p.id}`}
              className="w-full text-left bg-white border border-[#E2E8F0] rounded-2xl p-4 hover:border-neutral-400 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{p.name}</p>
                  <p className="text-xs text-neutral-400">{webTypeLabel(p.type)} · {webStageLabel(p.stage)}</p>
                </div>
                <span className="text-xs text-neutral-500 shrink-0">{p.progress}%</span>
              </div>
              <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden mt-2">
                <div className="h-full bg-neutral-900 rounded-full" style={{ width: `${p.progress}%` }} />
              </div>
            </button>
          ))}
        </div>
      )}
      <WebProjectModal open={modalOpen} onClose={() => setModalOpen(false)} presetClientId={clientId}
        onCreated={(p) => { setModalOpen(false); navigate(`/sites-web/projet/${p.id}`); }} />
    </div>
  );
}


import StartTimerModal from "@/components/time/StartTimerModal";
import TimeEntryModal from "@/components/time/TimeEntryModal";
import ContentRequests from "@/components/ContentRequests";
import ReportsTab from "@/components/workspace/ReportsTab";
import LastReportCard from "@/components/workspace/LastReportCard";
import ClientFinanceTab from "@/components/workspace/ClientFinanceTab";
import InstagramConnect from "@/components/workspace/InstagramConnect";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BrandForm from "@/components/workspace/BrandForm";
import BillingSettings from "@/components/accounting/BillingSettings";
import ClientFinancials from "@/components/accounting/ClientFinancials";
import { IdeaGenerator, IdeaCard } from "@/components/workspace/IdeasTab";
import { frDate, platformLabel, formatLabel, statusOf, FORMATS, STATUSES, PLATFORMS, OBJECTIVES } from "@/lib/constants";
import ClientFicheModal from "@/components/ClientFicheModal";
import { toast } from "sonner";

function CalendarGen({ clientId, onDone }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState("8");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [formats, setFormats] = useState("reel,carrousel,photo");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/ai/clients/${clientId}/calendar/generate`, {
        month, count: Number(count), platforms: ["instagram"],
        formats: formats.split(","), objectives: ["engagement", "notoriete", "vente"],
      });
      toast.success(`${data.length} publications créées en brouillon pour ${month}`);
      setOpen(false);
      onDone?.();
    } catch { toast.error("La génération a échoué"); }
    finally { setLoading(false); }
  };

  if (!open) {
    return (
      <Button variant="outline" data-testid="generate-calendar-btn" onClick={() => setOpen(true)} className="gap-1.5">
        <Sparkles className="w-4 h-4" /> Créer mon calendrier du mois
      </Button>
    );
  }
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5 flex flex-wrap items-end gap-3 animate-fade-up" data-testid="calendar-generator">
      <div>
        <Label>Mois</Label>
        <Input type="month" data-testid="calgen-month" className="mt-1.5" value={month} onChange={(e) => setMonth(e.target.value)} />
      </div>
      <div>
        <Label>Nombre de publications</Label>
        <Select value={count} onValueChange={setCount}>
          <SelectTrigger data-testid="calgen-count" className="mt-1.5 w-40"><SelectValue /></SelectTrigger>
          <SelectContent>{["4", "8", "12", "16"].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Formats (séparés par des virgules)</Label>
        <Input data-testid="calgen-formats" className="mt-1.5 w-64" value={formats} onChange={(e) => setFormats(e.target.value)} />
      </div>
      <Button data-testid="calgen-submit" onClick={run} disabled={loading} className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5">
        <Sparkles className="w-4 h-4" /> {loading ? "Génération…" : "Générer le brouillon"}
      </Button>
      <button onClick={() => setOpen(false)} className="text-xs text-neutral-400 hover:text-neutral-900 pb-2">Annuler</button>
    </div>
  );
}

const RDV_REPORT_BADGE = {
  finalise: { label: "Compte rendu finalisé", cls: "bg-emerald-100 border-emerald-200 text-emerald-700" },
  envoye: { label: "Compte rendu envoyé", cls: "bg-blue-100 border-blue-200 text-blue-700" },
  brouillon: { label: "Compte rendu brouillon", cls: "bg-amber-100 border-amber-200 text-amber-700" },
};

export default function ClientWorkspace() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [pubs, setPubs] = useState([]);
  const [feed, setFeed] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [media, setMedia] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [modal, setModal] = useState({ open: false, date: null });
  const [copied, setCopied] = useState(false);
  const [feedFull, setFeedFull] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [timerOpen, setTimerOpen] = useState(false);
  const [timeEntryOpen, setTimeEntryOpen] = useState(false);
  const [finItems, setFinItems] = useState(null);
  const [schedItem, setSchedItem] = useState(null);
  const [schedForm, setSchedForm] = useState({ date: "", time: "09:00", duration: 60, occurrences: 6 });
  const [newFin, setNewFin] = useState({ label: "", amount: "", frequency: "mensuel", duration: 60 });
  const [recur, setRecur] = useState(null);

  const saveRecurring = async () => {
    try {
      await api.patch(`/clients/${clientId}`, {
        auto_schedule_enabled: recur?.auto_schedule_enabled ?? client.auto_schedule_enabled ?? false,
        recurring_work_minutes: Number(recur?.recurring_work_minutes ?? client.recurring_work_minutes) || 0,
        recurring_work_period: recur?.recurring_work_period ?? client.recurring_work_period ?? "semaine",
        preferred_day: recur?.preferred_day ?? client.preferred_day ?? "",
        preferred_period: recur?.preferred_period ?? client.preferred_period ?? "",
        allow_shooting_booking: recur?.allow_shooting_booking ?? client.allow_shooting_booking ?? true,
      });
      toast.success("Planification récurrente enregistrée — les créneaux seront réservés dans l'agenda");
      setRecur(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const finList = finItems ?? client?.billing_items ?? [];

  const saveFinancials = async () => {
    const monthly = finList.filter((i) => i.frequency === "mensuel");
    const oneOff = finList.filter((i) => i.frequency === "ponctuel");
    try {
      await api.patch(`/clients/${clientId}`, {
        billing_items: finList,
        billing_type: monthly.length ? "forfait" : oneOff.length ? "projet" : "",
        billing_amount: (monthly.length ? monthly : oneOff).reduce((s, i) => s + (Number(i.amount) || 0), 0),
      });
      toast.success("Données financières enregistrées");
      setFinItems(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const addFinItem = () => {
    if (!newFin.label.trim()) { toast.error("Libellé requis"); return; }
    setFinItems([...finList, {
      id: `${Date.now()}`, label: newFin.label.trim(),
      amount: Number(newFin.amount) || 0, frequency: newFin.frequency,
      duration_min: Number(newFin.duration) || 60,
    }]);
    setNewFin({ label: "", amount: "", frequency: "mensuel", duration: 60 });
  };

  const scheduleFinItem = async () => {
    if (!schedItem || !schedForm.date || !schedForm.time) { toast.error("Date et heure requises"); return; }
    const [h, m] = schedForm.time.split(":").map(Number);
    const start = h * 60 + m;
    try {
      const { data } = await api.post("/agenda/blocks/series", {
        title: `${schedItem.label} — ${client.name}`,
        client_id: clientId,
        date: schedForm.date,
        start_min: start,
        end_min: start + (Number(schedForm.duration) || 60),
        frequency: schedItem.frequency === "mensuel" ? "monthly" : "none",
        occurrences: Number(schedForm.occurrences) || 6,
        description: `Élément financier : ${schedItem.label} (${schedItem.amount} €${schedItem.frequency === "mensuel" ? "/mois" : ""})`,
      });
      toast.success(data.skipped?.length
        ? `${data.created} créneau(x) programmé(s) — ${data.skipped.length} ignoré(s) (conflit)`
        : `${data.created} créneau(x) programmé(s) dans l'agenda`);
      setSchedItem(null);
    } catch (e) { toast.error(apiError(e)); }
  };
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [rdvs, setRdvs] = useState([]);
  useEffect(() => {
    setSearchParams((p) => {
      const n = new URLSearchParams(p);
      if (tab === "overview") n.delete("tab"); else n.set("tab", tab);
      return n;
    }, { replace: true });
  }, [tab, setSearchParams]);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}`).then((r) => setClient(r.data)).catch(() => navigate("/clients"));
    api.get(`/publications?client_id=${clientId}`).then((r) => setPubs(r.data)).catch(() => {});
    api.get(`/clients/${clientId}/feed`).then((r) => setFeed(r.data)).catch(() => {});
    api.get(`/clients/${clientId}/ideas`).then((r) => setIdeas(r.data)).catch(() => {});
    api.get(`/media?client_id=${clientId}`).then((r) => setMedia(r.data)).catch(() => {});
    api.get("/tasks").then((r) => setTasks(r.data.filter((t) => t.client_id === clientId && !t.archived))).catch(() => {});
    api.get("/appointments").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setRdvs(list.filter((a) => a.client_id === clientId));
    }).catch(() => {});
  }, [clientId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Rafraîchissement périodique : calendrier et feed restent synchronisés
  useEffect(() => {
    const t = setInterval(() => {
      api.get(`/publications?client_id=${clientId}`).then((r) => setPubs(r.data)).catch(() => {});
      api.get(`/clients/${clientId}/feed`).then((r) => setFeed(r.data)).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [clientId]);

  if (!client) return <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;

  const onMove = async (id, date) => {
    setPubs((ps) => ps.map((p) => (p.id === id ? { ...p, date } : p)));
    setFeed((fs) => fs.map((p) => (p.id === id ? { ...p, date } : p)));
    try {
      await api.patch(`/publications/${id}`, { date });
      toast.success("Publication déplacée — le feed Instagram s'est mis à jour");
      api.get(`/clients/${clientId}/feed`).then((r) => setFeed(r.data));
    } catch { toast.error("Erreur"); load(); }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/clients/${clientId}/review-link`);
      const url = `${window.location.origin}/review/${data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Lien de validation client copié", { description: url });
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Ce client n'existe plus ou a été archivé. La liste a été actualisée.");
        window.dispatchEvent(new Event("sf:clients"));
        navigate("/clients");
      } else {
        toast.error(apiError(e));
      }
    }
  };

  const archive = async () => {
    await api.delete(`/clients/${clientId}`);
    toast.success("Client archivé");
    window.dispatchEvent(new Event("sf:clients"));
    navigate("/clients");
  };

  const uploadMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      try { await uploadFile(file, { client_id: clientId }); } catch { toast.error(`Échec : ${file.name}`); }
    }
    toast.success(`${files.length} fichier(s) ajouté(s)`);
    api.get(`/media?client_id=${clientId}`).then((r) => setMedia(r.data));
    e.target.value = "";
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await uploadFile(file);
      const url = data.url;
      await api.patch(`/clients/${clientId}`, { brand: { ...(client.brand || {}), logo_url: url } });
      toast.success("Logo mis à jour");
      load();
    } catch { toast.error("Échec du téléversement"); }
    e.target.value = "";
  };

  const uploadBrandFile = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      try { await uploadFile(file, { client_id: clientId, category: "marque" }); } catch { toast.error(`Échec : ${file.name}`); }
    }
    if (files.length) toast.success(`${files.length} fichier(s) de marque ajouté(s)`);
    api.get(`/media?client_id=${clientId}`).then((r) => setMedia(r.data));
    e.target.value = "";
  };

  const reviewUrl = client.review_token ? `${window.location.origin}/review/${client.review_token}` : null;
  const upcoming = pubs.filter((p) => p.date >= new Date().toISOString().slice(0, 10) && p.status !== "publie").slice(0, 5);
  const mods = pubs.filter((p) => p.status === "modification");
  const doneStatuses = ["programme", "publie"];
  const monday = new Date(); monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const weekStart = monday.toISOString().slice(0, 10);
  const weekEnd = new Date(monday.getTime() + 6 * 86400000).toISOString().slice(0, 10);
  const weekDone = pubs.filter((p) => doneStatuses.includes(p.status) && p.date >= weekStart && p.date <= weekEnd).length;
  const monthDone = pubs.filter((p) => doneStatuses.includes(p.status) && (p.date || "").startsWith(new Date().toISOString().slice(0, 7))).length;
  const statusCount = (s) => pubs.filter((p) => p.status === s).length;

  return (
    <div className="space-y-6" data-testid="client-workspace">
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <label
          className="relative w-12 h-12 rounded-xl flex items-center justify-center text-white font-heading font-bold text-xl shrink-0 cursor-pointer group overflow-hidden"
          style={{ background: client.color }} data-testid="client-logo-upload" title="Changer le logo">
          {client.brand?.logo_url ? <img src={client.brand.logo_url} className="w-full h-full object-cover rounded-xl" alt="" /> : client.name[0]}
          <span className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl">
            <Upload className="w-4 h-4 text-white" />
          </span>
          <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
        </label>
        <div className="flex-1 min-w-0 basis-[calc(100%-3.75rem)] sm:basis-auto">
          <button data-testid="client-name" title="Ouvrir les réglages du client"
            onClick={() => { setSettingsOpen(true); }}
            className="font-heading text-2xl sm:text-3xl font-bold text-left group flex items-center gap-2 hover:text-neutral-600 transition-colors max-w-full">
            <span className="truncate">{client.name}</span>
            <Pencil className="w-4 h-4 shrink-0 text-neutral-300 group-hover:text-neutral-500 transition-colors" />
          </button>
          <p className="text-sm text-neutral-500 truncate">{client.sector}</p>
          <div className="flex flex-wrap gap-1 mt-1" data-testid="client-prestations">
            {(client.services?.length ? client.services : legacyServices(client.service_type)).map((sv) => (
              <span key={sv} className="text-[10px] border border-neutral-200 bg-neutral-50 rounded-full px-2 py-0.5 text-neutral-500" data-testid={`prestation-badge-${sv}`}>
                {prestationLabel(sv)}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" data-testid="open-fiche-btn" onClick={() => setSettingsOpen(true)} className="gap-1.5">
            <Pencil className="w-4 h-4" /> Voir / Modifier la fiche client
          </Button>
          <Button variant="outline" data-testid="goto-params" onClick={() => setTab("params")} className="gap-1.5" title="Paramètres du client">
            <Settings2 className="w-4 h-4" /> Paramètres client
          </Button>
          <Button variant="outline" data-testid="start-timer-btn" onClick={() => setTimerOpen(true)} className="gap-1.5">
            <Play className="w-4 h-4" /> Chrono
          </Button>
          <Button variant="outline" data-testid="add-time-btn" onClick={() => setTimeEntryOpen(true)} className="gap-1.5">
            <Clock className="w-4 h-4" /> Temps
          </Button>
          <CalendarGen clientId={clientId} onDone={load} />
          <Button data-testid="share-client-btn" onClick={share} className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5">
            {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />} Partager avec le client
          </Button>
          <Button variant="outline" size="icon" data-testid="share-client-btn-icon2" onClick={share} title="Partager avec le client" className="hidden" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex gap-1 border-b border-neutral-200 overflow-x-auto" data-testid="client-main-nav">
          {[["overview", "Vue d'ensemble", "overview"], ["apercu", "Community Management", "cm"],
            ["graphisme", "Graphisme", "graphisme"], ["web", "Web Design", "web"]].map(([v, l, sec]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`client-nav-${sec}`}
              className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${sectionOf(tab) === sec ? "border-[#0B132B] font-semibold text-[#0F172A]" : "border-transparent text-neutral-400 hover:text-neutral-900"}`}>
              {l}
            </button>
          ))}
        </div>
        {sectionOf(tab) === "cm" && (
          <div className="flex gap-1.5 overflow-x-auto pt-3" data-testid="client-cm-subnav">
            {CM_SUBNAV.map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${tab === v ? "bg-[#0B132B] text-white" : "text-neutral-500 hover:text-neutral-900 bg-white border border-[#E2E8F0]"}`}>
                {l}
              </button>
            ))}
          </div>
        )}

        <TabsContent value="overview" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4" data-testid="client-overview">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 space-y-2">
              <h3 className="font-heading font-bold">Informations client</h3>
              {(client.contact_first_name || client.contact_last_name) && (
                <p className="text-sm"><span className="text-neutral-400">Contact :</span> {[client.contact_first_name, client.contact_last_name].filter(Boolean).join(" ")}</p>
              )}
              {client.email && <p className="text-sm"><span className="text-neutral-400">Email :</span> {client.email}</p>}
              {client.phone && <p className="text-sm"><span className="text-neutral-400">Téléphone :</span> {client.phone}</p>}
              {client.sector && <p className="text-sm"><span className="text-neutral-400">Activité :</span> {client.sector}</p>}
              {(client.city || client.address) && (
                <p className="text-sm"><span className="text-neutral-400">Adresse :</span> {[client.address, client.zip_code, client.city].filter(Boolean).join(", ")}</p>
              )}
              <div className="flex flex-wrap gap-1 pt-1">
                {(client.services?.length ? client.services : legacyServices(client.service_type)).map((sv) => (
                  <span key={sv} className="text-[10px] border border-neutral-200 bg-neutral-50 rounded-full px-2 py-0.5 text-neutral-500">{prestationLabel(sv)}</span>
                ))}
              </div>
              <div className="flex gap-2 pt-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)} data-testid="overview-edit-fiche">Voir / Modifier la fiche client</Button>
                <Button size="sm" variant="outline" onClick={() => setTab("params")} data-testid="overview-params">Paramètres client</Button>
              </div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5">
              <h3 className="font-heading font-bold mb-3">Accès rapide</h3>
              <div className="grid grid-cols-2 gap-2">
                {[["stats", "Statistiques"], ["rapports", "Rapports"], ["medias", "Médias"],
                  ["idees", "Idées"], ["finances", "Finances"], ["marque", "Identité de marque"], ["todo", "To-do"]].map(([v, l]) => (
                  <button key={v} onClick={() => setTab(v)} data-testid={`overview-goto-${v}`}
                    className="border border-neutral-200 rounded-lg px-3 py-2.5 text-sm text-left hover:border-neutral-400 transition-colors">{l} →</button>
                ))}
              </div>
            </div>
            <LastReportCard clientId={clientId} />
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 md:col-span-2" data-testid="overview-documents">
              <h3 className="font-heading font-bold mb-1">Documents</h3>
              <p className="text-xs text-neutral-400 mt-0.5 mb-4">Espace de fichiers partagé avec le client — les dossiers et fichiers sont synchronisés avec son espace.</p>
              <DriveExplorer side="agency" clientId={clientId} />
            </div>
            {rdvs.length > 0 && (
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 md:col-span-2" data-testid="client-rdv-section">
                <h3 className="font-heading font-bold mb-2">Rendez-vous</h3>
                {rdvs.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 py-2.5 text-sm border-t border-neutral-100 first:border-0"
                    data-testid={`client-rdv-${r.id}`}>
                    <span className="truncate"><strong>{r.date.slice(8, 10)}/{r.date.slice(5, 7)}/{r.date.slice(0, 4)}</strong> · {r.title}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      {r.date < new Date().toISOString().slice(0, 10) && (() => {
                        const badge = RDV_REPORT_BADGE[r.report_status] || { label: "Compte rendu à rédiger", cls: "bg-amber-50 border-amber-200 text-amber-700" };
                        return (
                          <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${badge.cls}`}
                            data-testid={`client-rdv-report-${r.id}`}>{badge.label}</span>
                        );
                      })()}
                      <span className="text-xs text-neutral-500">{r.type_label}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="apercu" className="mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { l: "Publications ce mois", v: client.stats?.month_count ?? 0 },
              { l: "En attente de validation", v: statusCount("a_valider") },
              { l: "Validées", v: statusCount("valide") + statusCount("programme") },
              { l: "Publiées", v: statusCount("publie") },
            ].map((s) => (
              <div key={s.l} className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid={`client-stat-${s.l}`}>
                <p className="font-heading text-3xl font-bold">{s.v}</p>
                <p className="text-xs text-neutral-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
          {statusCount("a_valider") > 0 && (
            <div className="mb-6 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid="overview-pending-validation">
              <p className="text-sm text-orange-800">
                <b>{statusCount("a_valider")} publication(s)</b> en attente de validation par le client
              </p>
              <NotifyClientButton clientId={clientId} pendingCount={statusCount("a_valider")} />
            </div>
          )}
          {(Number(client.objective_week) > 0 || Number(client.objective_month) > 0) && (
            <div className="mb-6 bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid="overview-objectives">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-4 h-4 text-neutral-400" />
                <h3 className="font-heading font-bold">Objectifs de publication</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { label: "Objectif semaine", done: weekDone, goal: Number(client.objective_week) || 0, tid: "week" },
                  { label: "Objectif mois", done: monthDone, goal: Number(client.objective_month) || 0, tid: "month" },
                ].filter((o) => o.goal > 0).map((o) => {
                  const pct = Math.min(100, Math.round((o.done / o.goal) * 100));
                  const reached = o.done >= o.goal;
                  return (
                    <div key={o.tid} data-testid={`objective-${o.tid}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm text-neutral-500">{o.label}</p>
                        <p className={`flex items-center gap-1 text-sm font-semibold ${reached ? "text-green-600" : "text-neutral-900"}`} data-testid={`objective-${o.tid}-count`}>
                          {o.done}/{o.goal}{reached && <> <Check className="w-3.5 h-3.5" /> atteint</>}
                        </p>
                      </div>
                      <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${reached ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mb-6">
            <div className="flex justify-end -mb-1">
              <button onClick={() => navigate(`/taches?client=${clientId}`)} data-testid="client-tasks-see-all"
                className="text-[11px] font-semibold text-[#1D4ED8] hover:underline">Voir toutes les tâches du client →</button>
            </div>
            <TodoCard tasks={tasks} onChanged={load} clientId={clientId} />
          </div>
          <SocialNetworksAdmin clientId={clientId} />
          <MetaConnect clientId={clientId} />
          {mods.length > 0 && (
            <div className="mb-6 bg-red-50/60 border border-red-200 rounded-xl shadow-sm overflow-hidden" data-testid="overview-modifications">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-red-100">
                <h3 className="font-heading font-bold text-red-800">Modifications demandées par le client ({mods.length})</h3>
                <button className="text-xs text-neutral-500 hover:text-neutral-900" onClick={() => setTab("publications")} data-testid="goto-modifications">Tout voir →</button>
              </div>
              {mods.map((p) => (
                <button key={p.id} data-testid={`modif-${p.id}`} onClick={() => navigate(`/publications/${p.id}`)}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-red-50 border-b border-red-100 last:border-0 text-left transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.title}</p>
                    <p className="text-xs text-neutral-400">{frDate(p.date)} · {p.time} · {platformLabel(p.platform)} {formatLabel(p.format)}</p>
                  </div>
                  <StatusBadge status={p.status} />
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
                <h3 className="font-heading font-bold">Prochaines publications</h3>
                <div className="flex items-center gap-3">
                  <button data-testid="overview-add-pub" onClick={() => setModal({ open: true, date: null })}
                    className="flex items-center gap-1 text-xs font-medium bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-md px-2.5 py-1.5 transition-colors">
                    <Plus className="w-3.5 h-3.5" /> Ajouter une publication
                  </button>
                  <button className="text-xs text-neutral-500 hover:text-neutral-900" onClick={() => setTab("calendrier")} data-testid="goto-calendar">Calendrier →</button>
                </div>
              </div>
              {upcoming.length === 0 && (
                <div className="p-8 text-center">
                  <p className="text-sm text-neutral-400 mb-4">Votre calendrier est encore vide.</p>
                  <Button size="sm" data-testid="empty-create-pub" onClick={() => setModal({ open: true, date: null })} className="bg-neutral-900 text-white gap-1"><Plus className="w-3.5 h-3.5" /> Créer une publication</Button>
                </div>
              )}
              {upcoming.map((p) => (
                <button key={p.id} data-testid={`upcoming-${p.id}`} onClick={() => navigate(`/publications/${p.id}`)}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-neutral-50 border-b border-neutral-100 last:border-0 text-left transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.title}</p>
                    <p className="text-xs text-neutral-400">{frDate(p.date)} · {p.time} · {platformLabel(p.platform)} {formatLabel(p.format)}</p>
                  </div>
                  <StatusBadge status={p.status} />
                </button>
              ))}
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
                <h3 className="font-heading font-bold">Aperçu du feed Instagram</h3>
                <button className="text-xs text-neutral-500 hover:text-neutral-900" onClick={() => setTab("feed")} data-testid="goto-feed">Agrandir →</button>
              </div>
              <div className="p-4">
                <FeedGrid pubs={feed.slice(0, 6)} clientColor={client.color} onPubClick={(p) => navigate(`/publications/${p.id}`)} compact />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="graphisme" className="mt-6">
          <GraphismeClientFiche client={client} />
        </TabsContent>

        <TabsContent value="web" className="mt-6">
          <WebDesignTab clientId={clientId} />
        </TabsContent>

        <TabsContent value="calendrier" className="mt-6">          {pubs.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-12 text-center" data-testid="calendar-empty">
              <p className="font-heading font-bold text-lg">Votre calendrier est encore vide.</p>
              <p className="text-sm text-neutral-500 mt-1 mb-5">Générez un mois complet avec l'IA ou créez votre première publication.</p>
              <div className="flex gap-2 justify-center">
                <Button data-testid="empty-create-pub-2" onClick={() => setModal({ open: true, date: null })} className="bg-neutral-900 text-white gap-1"><Plus className="w-4 h-4" /> Créer une publication</Button>
              </div>
              <div className="mt-4 max-w-xl mx-auto"><CalendarGen clientId={clientId} onDone={load} /></div>
            </div>
          ) : (
            <CalendarMonth pubs={pubs} colorOf={() => client.color} month={month} setMonth={setMonth}
              onPubClick={(p) => navigate(`/publications/${p.id}`)}
              onDayClick={(date) => setModal({ open: true, date })}
              onMove={onMove} />
          )}
        </TabsContent>

        <TabsContent value="finances" className="mt-6">
          <ClientFinanceTab clientId={clientId} />
        </TabsContent>

        <TabsContent value="publications" className="mt-6 space-y-4">
          <div className="flex items-center justify-between" data-testid="pubs-table">
            <h2 className="font-heading font-bold text-xl">Publications</h2>
            <Button data-testid="add-publication-btn" onClick={() => setModal({ open: true, date: null })}
              className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5">
              <Plus className="w-4 h-4" /> Ajouter une publication
            </Button>
          </div>
          {pubs.filter((p) => p.status === "a_valider").length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid="pubs-pending-validation-banner">
              <p className="text-sm text-orange-800">
                <b>{pubs.filter((p) => p.status === "a_valider").length} publication(s)</b> en attente de validation
              </p>
              <NotifyClientButton clientId={clientId} pendingCount={pubs.filter((p) => p.status === "a_valider").length} />
            </div>
          )}
          <PublicationsTable pubs={pubs} onOpen={(p) => navigate(`/publications/${p.id}`)} onChanged={load} testPrefix="cw" />
        </TabsContent>

        <TabsContent value="idees" className="mt-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-xl">Idées de publications</h2>
            <IdeaGenerator clientId={clientId} onGenerated={() => api.get(`/clients/${clientId}/ideas`).then((r) => setIdeas(r.data))} />
          </div>
          {ideas.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-12 text-center" data-testid="ideas-empty">
              <Sparkles className="w-8 h-8 mx-auto text-neutral-300 mb-3" />
              <p className="font-heading font-bold">Aucune idée pour le moment</p>
              <p className="text-sm text-neutral-500 mt-1">L'IA utilisera l'identité de marque de {client.name} pour proposer des idées pertinentes.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {ideas.map((idea, i) => <IdeaCard key={idea.id} idea={idea} color={client.color} index={i} onRefresh={load} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="todo" className="mt-6">
          <div className="max-w-2xl">
            <div className="flex justify-end -mb-1">
              <button onClick={() => navigate(`/taches?client=${clientId}`)} data-testid="client-tasks-see-all"
                className="text-[11px] font-semibold text-[#1D4ED8] hover:underline">Voir toutes les tâches du client →</button>
            </div>
            <TodoCard tasks={tasks} onChanged={load} clientId={clientId} />
          </div>
        </TabsContent>

        <TabsContent value="contenus" className="mt-6">
          <ContentRequests client={client} pubs={pubs} />
        </TabsContent>
        <TabsContent value="feed" className="mt-6">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-6 max-w-2xl">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-neutral-100">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-heading font-bold" style={{ background: client.color }}>
                {client.brand?.logo_url ? <img src={client.brand.logo_url} className="w-full h-full object-cover rounded-full" alt="" /> : client.name[0]}
              </div>
              <div>
                <p className="font-semibold text-sm">{client.name.toLowerCase().replace(/\s/g, "")}</p>
                <p className="text-xs text-neutral-400">{feed.length} publications · aperçu synchronisé avec le calendrier</p>
              </div>
            </div>
            <FeedGrid pubs={feed} clientColor={client.color} onPubClick={(p) => navigate(`/publications/${p.id}`)} showDate />
          </div>
        </TabsContent>

        <TabsContent value="marque" className="mt-6">
          <BrandForm client={client} onSaved={load} />
        </TabsContent>

        <TabsContent value="medias" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl">Bibliothèque média</h2>
            <label>
              <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={uploadMedia} data-testid="media-upload-input" />
              <span className="inline-flex items-center gap-1.5 bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-md px-4 py-2 text-sm cursor-pointer transition-colors">
                <Plus className="w-4 h-4" /> Téléverser
              </span>
            </label>
          </div>
          {media.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-12 text-center" data-testid="media-empty">
              <p className="text-sm text-neutral-400">Aucun média. Téléversez photos, vidéos et logos de {client.name}.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
              {media.map((m) => (
                <div key={m.id} className="relative group rounded-xl overflow-hidden border border-neutral-200 bg-white aspect-square" data-testid={`media-${m.id}`}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt={m.name} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">{m.name}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="stats" className="mt-6">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-6 max-w-2xl" data-testid="client-stats">
            <h3 className="font-heading font-bold mb-4">Répartition par statut</h3>
            <div className="space-y-2.5">
              {STATUSES.map((s) => {
                const n = statusCount(s.id);
                const pct = pubs.length ? Math.round((n / pubs.length) * 100) : 0;
                return (
                  <div key={s.id} className="flex items-center gap-3">
                    <span className="text-sm w-40 text-neutral-600">{s.label}</span>
                    <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: client.color }} />
                    </div>
                    <span className="text-sm font-medium w-8 text-right">{n}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-6">
            <ClientFinancials clientId={clientId} />
          </div>
        </TabsContent>

        <TabsContent value="rapports" className="mt-6">
          <ReportsTab clientId={clientId} clientName={client.name} />
        </TabsContent>

        <TabsContent value="params" className="mt-6">
          <div className="max-w-2xl space-y-3" data-testid="client-params">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid="params-brand-summary">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-14 h-14 rounded-xl border border-neutral-200 flex items-center justify-center text-white font-heading font-bold text-xl overflow-hidden shrink-0" style={{ background: client.color }} data-testid="params-logo-preview">
                  {client.brand?.logo_url ? <img src={client.brand.logo_url} className="w-full h-full object-cover" alt="" /> : client.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-sm">Identité de marque</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="w-4 h-4 rounded-full border border-neutral-200 inline-block" style={{ background: client.color }} />
                    <code className="text-xs text-neutral-500">{client.color}</code>
                    <button onClick={() => { navigator.clipboard.writeText(client.color); toast.success("Code couleur copié"); }}
                      className="text-neutral-300 hover:text-neutral-900 transition-colors" data-testid="copy-brand-color" title="Copier le code HEX">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-[11px] text-neutral-400">· {media.filter((m) => m.category === "marque").length} fichier(s) de marque</span>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setBrandOpen(true)} data-testid="open-brand-modal">
                  {client.brand?.logo_url || media.some((m) => m.category === "marque") ? "Voir / Modifier l'identité de marque" : "+ Ajouter l'identité de marque"}
                </Button>
              </div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-5" data-testid="params-financials-section">
              <h3 className="font-heading font-bold mb-1">Données financières</h3>
              <p className="text-sm text-neutral-500 mb-3">Forfaits mensuels et travaux ponctuels facturables pour ce client.</p>
              <div className="space-y-1.5 mb-3">
                {finList.length === 0 && <p className="text-sm text-neutral-400" data-testid="fin-empty">Aucun élément. Ajoutez un forfait mensuel, un travail ponctuel…</p>}
                {finList.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 border border-neutral-100 rounded-lg px-3 py-2 group" data-testid={`fin-item-${item.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.label}</p>
                      <p className="text-[11px] text-neutral-400">
                        {item.amount} €{item.frequency === "mensuel" ? " / mois" : " (ponctuel)"} · ~{item.duration_min} min
                      </p>
                    </div>
                    <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${item.frequency === "mensuel" ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-neutral-100 border-neutral-200 text-neutral-600"}`}>
                      {item.frequency === "mensuel" ? "Mensuel" : "Ponctuel"}
                    </span>
                    <button data-testid={`fin-item-schedule-${item.id}`} title="Programmer dans l'agenda"
                      onClick={() => { setSchedItem(item); setSchedForm({ date: "", time: "09:00", duration: item.duration_min || 60, occurrences: 6 }); }}
                      className="text-neutral-300 hover:text-neutral-900 transition-colors shrink-0">
                      <CalendarPlus className="w-4 h-4" />
                    </button>
                    <button data-testid={`fin-item-delete-${item.id}`} title="Retirer"
                      onClick={() => setFinItems(finList.filter((i) => i.id !== item.id))}
                      className="text-neutral-300 hover:text-red-500 transition-colors shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <Input data-testid="fin-label" placeholder="Libellé (ex : Community management)" value={newFin.label}
                  onChange={(e) => setNewFin((f) => ({ ...f, label: e.target.value }))} className="col-span-2" />
                <Input data-testid="fin-amount" type="number" min="0" placeholder="Montant €" value={newFin.amount}
                  onChange={(e) => setNewFin((f) => ({ ...f, amount: e.target.value }))} />
                <select data-testid="fin-frequency" className="text-sm border border-neutral-200 rounded-md px-2 py-2 bg-white"
                  value={newFin.frequency} onChange={(e) => setNewFin((f) => ({ ...f, frequency: e.target.value }))}>
                  <option value="mensuel">Forfait mensuel</option>
                  <option value="ponctuel">Travail ponctuel</option>
                </select>
                <Input data-testid="fin-duration" type="number" min="15" step="15" placeholder="Durée (min)" title="Temps prévu par occurrence (minutes)" value={newFin.duration}
                  onChange={(e) => setNewFin((f) => ({ ...f, duration: e.target.value }))} />
                <Button data-testid="fin-add" onClick={addFinItem} variant="outline" className="gap-1.5 col-span-2 sm:col-span-1">
                  <Plus className="w-4 h-4" /> Ajouter
                </Button>
              </div>
              <Button data-testid="billing-save" onClick={saveFinancials} size="sm" className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white">Enregistrer</Button>
            </div>

            <ParamSection title="Facturation" desc="Coordonnées de facturation (SIRET / IDE-CH), TVA et factures automatiques" testId="params-billing">
              <BillingSettings client={client} onSaved={load} />
            </ParamSection>

            <Dialog open={!!schedItem} onOpenChange={(v) => !v && setSchedItem(null)}>
              <DialogContent className="max-w-sm" data-testid="fin-schedule-dialog" aria-describedby={undefined}>
                <DialogHeader>
                  <DialogTitle className="font-heading">Programmer dans l'agenda</DialogTitle>
                </DialogHeader>
                {schedItem && (
                  <div className="space-y-3">
                    <p className="text-sm text-neutral-600">
                      <strong>{schedItem.label}</strong> — {schedItem.amount} €
                      {schedItem.frequency === "mensuel" ? " / mois" : " (ponctuel)"}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>{schedItem.frequency === "mensuel" ? "1ère occurrence" : "Date"}</Label>
                        <Input type="date" data-testid="fin-schedule-date" className="mt-1.5" value={schedForm.date}
                          onChange={(e) => setSchedForm((f) => ({ ...f, date: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Heure</Label>
                        <Input type="time" data-testid="fin-schedule-time" className="mt-1.5" value={schedForm.time}
                          onChange={(e) => setSchedForm((f) => ({ ...f, time: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Durée (min)</Label>
                        <Input type="number" min="15" step="15" data-testid="fin-schedule-duration" className="mt-1.5" value={schedForm.duration}
                          onChange={(e) => setSchedForm((f) => ({ ...f, duration: e.target.value }))} />
                      </div>
                      {schedItem.frequency === "mensuel" && (
                        <div>
                          <Label>Répéter (mois)</Label>
                          <Input type="number" min="1" max="12" data-testid="fin-schedule-occurrences" className="mt-1.5" value={schedForm.occurrences}
                            onChange={(e) => setSchedForm((f) => ({ ...f, occurrences: e.target.value }))} />
                        </div>
                      )}
                    </div>
                    {schedItem.frequency === "mensuel" && (
                      <p className="text-[11px] text-neutral-400">Un créneau sera créé chaque mois au même jour/heure. Les créneaux déjà occupés sont ignorés automatiquement.</p>
                    )}
                    <Button data-testid="fin-schedule-confirm" onClick={scheduleFinItem}
                      className="w-full bg-[#0B132B] hover:bg-[#1C2A4F] text-white">
                      Programmer dans l'agenda
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            <ParamSection title="Accès à l'espace client" desc="Identifiants du portail client DGA, plateformes autorisées et utilisateurs additionnels" testId="params-portal-access" defaultOpen>
              <GraphismeAccessCard clientId={clientId} />
              <GraphismeUsersCard clientId={clientId} />
            </ParamSection>

            <ParamSection title="Lien de validation CM (sans compte)" desc="Le client accède à ses contenus sans compte : consultation, commentaires, validation" testId="params-access">

              {reviewUrl ? (
                <div className="flex gap-2">
                  <Input readOnly value={reviewUrl} data-testid="review-url" className="font-mono text-xs" />
                  <Button variant="outline" data-testid="copy-review-url" onClick={share}>{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button>
                </div>
              ) : (
                <Button onClick={share} data-testid="create-review-link" className="bg-neutral-900 text-white">Générer le lien</Button>
              )}
            </ParamSection>
            <ParamSection title="Instagram" desc="Connexion aux statistiques Instagram du client" testId="params-instagram">
              <InstagramConnect clientId={clientId} />
            </ParamSection>
            <ParamSection title="Temps récurrent (contrat)" desc="Des créneaux sont automatiquement réservés chaque semaine dans votre agenda pour ce client" testId="recurring-section">
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input type="checkbox" className="accent-neutral-900" data-testid="recurring-enabled"
                  checked={recur?.auto_schedule_enabled ?? client.auto_schedule_enabled ?? false}
                  onChange={(e) => setRecur((r) => ({ ...(r || {}), auto_schedule_enabled: e.target.checked }))} />
                <span className="text-sm">Planifier automatiquement le temps contractuel</span>
              </label>
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input type="checkbox" className="accent-neutral-900" data-testid="shooting-booking-enabled"
                  checked={recur?.allow_shooting_booking ?? client.allow_shooting_booking ?? true}
                  onChange={(e) => setRecur((r) => ({ ...(r || {}), allow_shooting_booking: e.target.checked }))} />
                <span className="text-sm">Autoriser le client à réserver lui-même ses shootings (créneaux réels de l'agenda)</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Temps contractuel (heures)</Label>
                  <Input data-testid="recurring-minutes" type="number" min="0" step="0.5" className="mt-1.5"
                    value={(() => { const m = recur?.recurring_work_minutes ?? client.recurring_work_minutes ?? 0; return m ? m / 60 : ""; })()}
                    onChange={(e) => setRecur((r) => ({ ...(r || {}), recurring_work_minutes: Math.round((Number(e.target.value) || 0) * 60) }))} />
                </div>
                <div>
                  <Label>Période</Label>
                  <select data-testid="recurring-period" className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white"
                    value={recur?.recurring_work_period ?? client.recurring_work_period ?? "semaine"}
                    onChange={(e) => setRecur((r) => ({ ...(r || {}), recurring_work_period: e.target.value }))}>
                    <option value="semaine">Par semaine</option>
                    <option value="mois">Par mois</option>
                  </select>
                </div>
                <div>
                  <Label>Jour préféré</Label>
                  <select data-testid="recurring-day" className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white"
                    value={String(recur?.preferred_day ?? client.preferred_day ?? "")}
                    onChange={(e) => setRecur((r) => ({ ...(r || {}), preferred_day: e.target.value }))}>
                    <option value="">Aucun (le plus tôt possible)</option>
                    {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((d, i) => (
                      <option key={i} value={String(i)}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Moment préféré</Label>
                  <select data-testid="recurring-moment" className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white"
                    value={recur?.preferred_period ?? client.preferred_period ?? ""}
                    onChange={(e) => setRecur((r) => ({ ...(r || {}), preferred_period: e.target.value }))}>
                    <option value="">Indifférent</option>
                    <option value="morning">Matin</option>
                    <option value="afternoon">Après-midi</option>
                  </select>
                </div>
              </div>
              <Button data-testid="recurring-save" onClick={saveRecurring} size="sm" className="mt-3 bg-[#0B132B] hover:bg-[#1C2A4F] text-white">Enregistrer</Button>
            </ParamSection>
            <ParamSection title="Zone sensible" testId="params-danger" defaultOpen>
              <Button variant="outline" data-testid="archive-client" onClick={archive} className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50">
                <Archive className="w-4 h-4" /> Archiver ce client
              </Button>
            </ParamSection>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={brandOpen} onOpenChange={setBrandOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="brand-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">Identité de marque — {client.name}</DialogTitle>
          </DialogHeader>
          <BrandForm client={client} onSaved={() => { setBrandOpen(false); load(); }} />
        </DialogContent>
      </Dialog>

      <ClientFicheModal open={settingsOpen} onClose={() => setSettingsOpen(false)} client={client}
        onSaved={() => { setSettingsOpen(false); window.dispatchEvent(new Event("sf:clients")); load(); }} />

      <NewPublicationModal open={modal.open} setOpen={(v) => setModal({ ...modal, open: v })}
        clients={[client]} defaultClientId={clientId} defaultDate={modal.date} onCreated={load} />
      <StartTimerModal open={timerOpen} setOpen={setTimerOpen} client={client} />
      <TimeEntryModal open={timeEntryOpen} setOpen={setTimeEntryOpen} clients={[client]} defaultClientId={clientId} onSaved={load} />

      <button
        data-testid="view-feed-final"
        onClick={() => setFeedFull(true)}
        className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-40 flex items-center gap-2 bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-full px-5 py-3 text-sm font-medium shadow-lg transition-transform hover:scale-105"
      >
        <Images className="w-4 h-4" /> Visualiser le feed final
      </button>

      {feedFull && (
        <div className="fixed inset-0 z-50 bg-neutral-50 overflow-y-auto animate-fade-up" data-testid="feed-fullscreen">
          <div className="max-w-3xl mx-auto px-4 py-6">
            <div className="flex items-center gap-3 mb-6 bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-sm">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-heading font-bold overflow-hidden" style={{ background: client.color }}>
                {client.brand?.logo_url ? <img src={client.brand.logo_url} className="w-full h-full object-cover" alt="" /> : client.name[0]}
              </div>
              <div>
                <p className="font-semibold">{client.name}</p>
                <p className="text-xs text-neutral-400">{feed.length} publications · feed final</p>
              </div>
              <button data-testid="feed-fullscreen-close" onClick={() => setFeedFull(false)}
                className="ml-auto p-2 rounded-md hover:bg-neutral-100 text-neutral-500 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <FeedGrid pubs={feed} clientColor={client.color} showDate
              onPubClick={(p) => { setFeedFull(false); navigate(`/publications/${p.id}`); }} />
          </div>
        </div>
      )}
    </div>
  );
}
